import { FormUnit } from "@foodpilot/foods";
import { Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { useMyActionPlanContext } from "src/components/Actions/Tabs/2_TabActionPlan/context/useMyActionPlan";
import { ExtendedImpactData } from "../../../../context/utils/impactData/formatComparisonTable";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { ImpactComparison } from "../Common/ImpactComparison";
import { currentActionMarkerSx, EngagedActionTitle } from "../Common/EngagedActionTitle";

type Props = {
    extendedImpactData: ExtendedImpactData[];
    total: number | null;
};
export const MultipleActionsRows = (props: Props) => {
    const { extendedImpactData, total } = props;

    const { lastSelectedVariation, selectedScorePrecision, formatScore, getColor } = useEntityScores();
    const theme = useTheme();
    const { t } = useTranslation();

    const groupedRows = Object.groupBy(extendedImpactData, (row) => row.title);

    const maxLineIndex = extendedImpactData.length - 1;
    const shouldShowTotal = extendedImpactData.length > 1;

    const _total = formatScore(total);
    return (
        <Grid2
            container
            columns={12}
            bgcolor={theme.custom.grey[100]}
            sx={{
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
                borderTop: `1px solid ${theme.custom.grey[600]}`,
            }}
        >
            {/* Header */}
            <Grid2
                size={12}
                container
                columns={12}
                bgcolor={theme.custom.grey[200]}
                height={"64px"}
                paddingInline={theme.spacing(2)}
                alignItems={"center"}
            >
                <Grid2 size={4}>
                    <Typography variant="body-bold">
                        {t("action_plan.action.engaged_action", "Engaged action")}
                    </Typography>
                </Grid2>
                <Grid2 size={4}>
                    <Typography variant="body-bold">{t("action_plan.action.indicators", "Indicator")}</Typography>
                </Grid2>
                <Grid2 size={4} textAlign={"end"}>
                    <Typography variant={"body-bold"}>{t("action_plan.action.impact", "Impact")}</Typography>
                </Grid2>
            </Grid2>

            {/* Content */}
            {Object.entries(groupedRows).map(([actionTitle, impactData], index) => {
                return (
                    <OneActionGroup
                        key={actionTitle}
                        lineNumber={index}
                        maxLineIndex={maxLineIndex}
                        shouldShowTotal={shouldShowTotal}
                        actionName={actionTitle}
                        impactData={impactData ?? []}
                    />
                );
            })}

            {/* Total Footer */}
            {_total !== null && (
                <Grid2
                    size={12}
                    container
                    columns={12}
                    bgcolor={theme.custom.grey[200]}
                    borderRadius="12px"
                    height={"64px"}
                    paddingInline={theme.spacing(2)}
                    alignItems={"center"}
                >
                    <Grid2 size={8}>
                        <Typography variant={"body-bold"}>{t("total", "Total")}</Typography>
                    </Grid2>
                    <Grid2 size={4} display={"inline-grid"} justifyItems={"end"}>
                        <Stack flexDirection={"row"}>
                            <ApproximateImpactTooltip
                                value={total}
                                precision={selectedScorePrecision}
                                unit={lastSelectedVariation.unit}
                            >
                                <FormUnit value={_total} unit={lastSelectedVariation.unit} color={getColor(total)} />
                            </ApproximateImpactTooltip>
                        </Stack>
                    </Grid2>
                </Grid2>
            )}
        </Grid2>
    );
};

type OneActionRowProps = {
    lineNumber: number;
    maxLineIndex: number;
    shouldShowTotal: boolean;
    actionName: string;
    impactData: ExtendedImpactData[];
};
const OneActionGroup = (props: OneActionRowProps) => {
    const { lineNumber, actionName, maxLineIndex, shouldShowTotal, impactData } = props;

    const { lastSelectedVariation } = useEntityScores();
    const { actionPlanAction } = useMyActionPlanContext();
    const theme = useTheme();

    const maxItems = impactData.length - 1;
    const hideBottomBorder = shouldShowTotal === false && maxLineIndex === lineNumber;
    const isSelectedAction = actionPlanAction.action.title === actionName;

    return (
        <Fragment>
            <Grid2
                size={4}
                alignContent={"center"}
                padding={theme.spacing(1, 2)}
                sx={[
                    {
                        borderTop: lineNumber === 0 ? `1px solid ${theme.custom.grey[600]}` : "none",
                        borderBottom: hideBottomBorder ? "none" : `1px solid ${theme.custom.grey[600]}`,
                        borderRight: `1px solid ${theme.custom.grey[600]}`,
                    },
                    isSelectedAction && currentActionMarkerSx(theme),
                ]}
            >
                <EngagedActionTitle
                    title={actionName}
                    isCurrentAction={isSelectedAction}
                    showCurrentActionTag={true}
                    otherActionVariant={"body"}
                />
            </Grid2>
            <Grid2 size={8} container columns={12}>
                {impactData.map((impact, index) => {
                    const hideBottomBorderSubgroup = hideBottomBorder && maxItems === index;
                    const score = impact.impactScores?.[lastSelectedVariation.scoreId];
                    return (
                        <Grid2
                            key={`${impact.propertyId}-${impact.actionId}-${impact.position}`}
                            size={12}
                            container
                            columns={12}
                            paddingInline={theme.spacing(2)}
                            minHeight={"64px"}
                            alignItems={"center"}
                            sx={{
                                borderTop:
                                    index === 0 && lineNumber === 0 ? `1px solid ${theme.custom.grey[600]}` : "none",
                                borderBottom: hideBottomBorderSubgroup ? "none" : `1px solid ${theme.custom.grey[600]}`,
                            }}
                        >
                            <Grid2 size={6}>
                                <Typography variant={"body"}>{impact.propertyLabel}</Typography>
                            </Grid2>
                            <Grid2 size={6} display={"inline-grid"} justifyItems={"end"}>
                                <ImpactComparison
                                    score={score ?? 0}
                                    value={{
                                        start: impact.startElement,
                                        end: impact.endElement,
                                        change: impact.elementChange,
                                        unit: impact.propertyUnit,
                                        fieldType: impact.fieldType,
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    );
                })}
            </Grid2>
        </Fragment>
    );
};
